import { TiptapContentNode } from '../../tiptapTypes';

/**
 * Recursively maps through Tiptap content tree, transforming each node.
 * This function first recurses to the leaf nodes, then maps each node on the way back up,
 * allowing transformations that depend on already-processed child nodes.
 *
 * @param tiptapNode The current node being processed
 * @param mapper A function that transforms each node, receives the node, processed children, and ancestors
 * @returns A new transformed Tiptap content structure
 */
export const mapTiptapNodes = (
    tiptapNode: TiptapContentNode,
    mapper: (node: TiptapContentNode, originalChildrenContent: TiptapContentNode[] | undefined) => TiptapContentNode,
): TiptapContentNode => {
    const originalChildrenContent = tiptapNode.content;
    // Process children first if they exist
    let mappedChildrenContent: TiptapContentNode[] | undefined;

    // Recurse the children if they exist
    if (originalChildrenContent && originalChildrenContent.length > 0) {
        let hasChanges = false;

        // Map each child node, passing the current node as part of ancestors
        mappedChildrenContent = originalChildrenContent.map((childNode) => {
            const mappedChildNode = mapTiptapNodes(childNode, mapper);
            if (mappedChildNode === childNode) return childNode;
            hasChanges = true;
            return mappedChildNode;
        });

        // If any child was changed, we need to update the content of the current node
        if (hasChanges) {
            tiptapNode = {
                ...tiptapNode,
                content: mappedChildrenContent,
            };
        }
    }

    // Then apply the mapper to this node with its mapped children
    return mapper(tiptapNode, originalChildrenContent);
};
